import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';
import api, { getScannerGate } from '../services/api';
import { getCmsImagePath } from '../cms/cmsData';
import { buildTicketSuccessState } from './TicketSuccess';
import ManualDashboardScanPanel from '../components/ManualDashboardScanPanel';
import './Scanner.css';

const Scanner = ({ requireStaffAuth = true, showManualSmartScan = false }) => {
  const [flashlightOn, setFlashlightOn] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [authChecking, setAuthChecking] = useState(requireStaffAuth);
  const [authError, setAuthError] = useState('');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const qrFrameRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const lastFrameTsRef = useRef(0);
  const canvasCtxRef = useRef(null);
  const scanningRef = useRef(false);
  const handleScanQRRef = useRef(null);
  const lastScanRef = useRef(null);
  const navigate = useNavigate();

  const scannerGate = getScannerGate();
  const physicalGateLabel = (
    scannerGate === 'Blue' || scannerGate === 'Green' || scannerGate === 'PAC AMP'
  ) ? scannerGate : '';
  const gateTone = physicalGateLabel === 'Blue'
    ? 'blue'
    : (physicalGateLabel === 'Green' ? 'green' : (physicalGateLabel === 'PAC AMP' ? 'pacamp' : ''));

  useEffect(() => {
    if (!requireStaffAuth) {
      setAuthChecking(false);
      setAuthError('');
      return;
    }

    // Check if user is authenticated and has staff permissions
    const checkAuth = async () => {
      if (!api.isAuthenticated()) {
        navigate('/login?redirect=/scanner', { replace: true });
        return;
      }
      
      // Verify user has staff permissions by checking their profile
      try {
        const response = await api.getProfile();
        if (!response.success) {
          setAuthError('Failed to verify permissions');
          setTimeout(() => navigate('/login?redirect=/scanner', { replace: true }), 2000);
          return;
        }
        
        const user = response.data;
        if (!user.is_staff) {
          setAuthError('Access denied. Scanner requires staff permissions.');
          setTimeout(() => navigate('/', { replace: true }), 3000);
          return;
        }
        
        setAuthChecking(false);
      } catch (err) {
        console.error('Auth check error:', err);
        setAuthError('Authentication error');
        setTimeout(() => navigate('/login?redirect=/scanner', { replace: true }), 2000);
      }
    };
    
    checkAuth();
  }, [navigate, requireStaffAuth]);

  useEffect(() => {
    if (authChecking) return;

    startCamera();
    return () => {
      stopCamera();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [authChecking]);

  useEffect(() => {
    const scannerBg = '#020617';
    const previousBodyBg = document.body.style.backgroundColor;
    const previousHtmlBg = document.documentElement.style.backgroundColor;
    const previousBodyOverscroll = document.body.style.overscrollBehaviorY;
    const previousHtmlOverscroll = document.documentElement.style.overscrollBehaviorY;

    document.body.style.backgroundColor = scannerBg;
    document.documentElement.style.backgroundColor = scannerBg;
    document.body.style.overscrollBehaviorY = 'none';
    document.documentElement.style.overscrollBehaviorY = 'none';

    return () => {
      document.body.style.backgroundColor = previousBodyBg;
      document.documentElement.style.backgroundColor = previousHtmlBg;
      document.body.style.overscrollBehaviorY = previousBodyOverscroll;
      document.documentElement.style.overscrollBehaviorY = previousHtmlOverscroll;
    };
  }, []);

  const startCamera = async () => {
    if (streamRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        videoRef.current.play?.().catch(() => {});
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const toggleFlashlight = async () => {
    if (!streamRef.current) return;

    try {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        const capabilities = videoTrack.getCapabilities();
        if (capabilities.torch) {
          await videoTrack.applyConstraints({
            advanced: [{ torch: !flashlightOn }]
          });
          setFlashlightOn(!flashlightOn);
        }
      }
    } catch (error) {
      console.error('Error toggling flashlight:', error);
    }
  };

  const handleVideoLoaded = () => {
    setVideoReady(true);
  };

  const navigateToSuccess = useCallback((resultData) => {
    navigate('/success', {
      state: buildTicketSuccessState(resultData),
    });
  }, [navigate]);

  // Commit and navigate helper
  const commitAndNavigate = useCallback(async (ticketCode, validationData, scanEventDate, rawQrData = '') => {
    try {
      const commitResult = await api.commitScan(ticketCode, 'both', scanEventDate, rawQrData);
      if (!commitResult?.success) {
        const msg = commitResult?.data?.message || commitResult?.error?.message || 'Could not mark ticket as used';
        if (commitResult?.data?.status === 'ALREADY_USED') {
          navigateToSuccess({
            ...(validationData || {}),
            ...(commitResult.data || {}),
          });
        } else {
          setError(msg);
          setTimeout(() => setError(''), 4000);
        }
        return;
      }
      const mergedData = { ...validationData, ...(commitResult?.data || {}) };
      navigateToSuccess(mergedData);
    } catch (commitErr) {
      const commitData = commitErr?.response?.data?.data || {};
      if (String(commitData.status || '').toUpperCase() === 'ALREADY_USED') {
        navigateToSuccess({
          ...(validationData || {}),
          ...commitData,
        });
        return;
      }

      console.error('Commit scan error:', commitErr);
      setError(commitData.message || 'Failed to check in ticket. Try again.');
      setTimeout(() => setError(''), 4000);
    }
  }, [navigateToSuccess]);

  const handleScanQR = useCallback(async (qrData) => {
    if (scanningRef.current || scanning || !qrData) return;
    
    // Prevent duplicate scans within a short window
    const now = Date.now();
    if (lastScanRef.current && (now - lastScanRef.current) < 1500) {
      return;
    }
    
    scanningRef.current = true;
    setScanning(true);
    setError('');
    lastScanRef.current = now;
    // Only training/test QRs (TESTQR-*) may simulate a specific festival day
    // via an embedded sed= param. Real attendee tickets are never date-shifted.
    let scanEventDate = null;
    try {
      if (typeof qrData === 'string' && qrData.includes('sed=') && /testqr/i.test(qrData)) {
        const sed = new URL(qrData).searchParams.get('sed');
        if (sed) scanEventDate = sed;
      }
    } catch (e) {
      scanEventDate = null;
    }
    
    try {
      const result = await api.validateScan(qrData, scanEventDate);
      console.log('Scan result:', result);
      
      if (result?.success) {
        if (result.data?.can_enter) {
          const ticketCode = result.data?.ticket_code;
          if (!ticketCode) { setScanning(false); return; }

          await commitAndNavigate(ticketCode, result.data, scanEventDate, qrData);
        } else {
          // Ticket found but can't enter (already used, scanned today, etc.)
          const blockedStatus = String(result.data?.status || '').toUpperCase();
          if (blockedStatus === 'ALREADY_USED' || blockedStatus === 'SCANNED_TODAY') {
            navigateToSuccess(result.data);
          } else {
            const errorMsg = result.data?.message || 'Ticket cannot be used for entry';
            setError(errorMsg);
            setTimeout(() => setError(''), 4000);
          }
        }
      } else {
        const errorMsg = result?.error?.message || result?.message || 'Invalid ticket';
        setError(errorMsg);
        setTimeout(() => setError(''), 4000);
      }
    } catch (err) {
      console.error('Scan error:', err);
      if (err.response?.status === 401) {
        setError(requireStaffAuth ? 'Authentication required. Please log in as staff.' : 'Scanner access denied for this origin.');
      } else if (err.response?.status === 403) {
        setError(requireStaffAuth ? 'Access denied. Staff permission required.' : 'Scanner access denied for this origin.');
      } else if (err.response?.status === 0) {
        setError('Network error. Check your connection.');
      } else {
        setError(err.message || 'Failed to validate ticket.');
      }
      setTimeout(() => setError(''), 4000);
    } finally {
      scanningRef.current = false;
      setScanning(false);
    }
  }, [scanning, commitAndNavigate, navigateToSuccess, requireStaffAuth]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleScanQRRef.current = handleScanQR;
  }, [handleScanQR]);

  useEffect(() => {
    scanningRef.current = scanning;
  }, [scanning]);

  // Detection loop: runs jsQR on a downscaled frame for fast, smooth scanning.
  const scanFrame = useCallback((timestamp) => {
    rafRef.current = requestAnimationFrame(scanFrame);

    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || scanningRef.current ||
        video.videoWidth === 0 || video.videoHeight === 0) {
      return;
    }

    // ~10 detections/sec: near-instant lock but low, steady CPU so it stays
    // fast across long scanning sessions (avoids thermal throttling lag).
    if (timestamp - lastFrameTsRef.current < 100) return;
    lastFrameTsRef.current = timestamp;

    try {
      const vw = video.videoWidth;
      const vh = video.videoHeight;

      // Region of interest = ONLY the on-screen green box. The camera feed uses
      // object-fit: cover, so we map the box's screen rect into the video's
      // intrinsic pixel coordinates and decode just that crop. This guarantees
      // we only read the QR the operator aligned inside the box and never a
      // neighboring QR elsewhere in frame (critical for the multi-QR qr-test
      // page, where several codes are visible at once).
      let sx = 0, sy = 0, sw = vw, sh = vh;
      const frameEl = qrFrameRef.current;
      const videoRect = video.getBoundingClientRect();
      if (frameEl && videoRect.width > 0 && videoRect.height > 0) {
        const frameRect = frameEl.getBoundingClientRect();
        const cover = Math.max(videoRect.width / vw, videoRect.height / vh);
        const offsetX = (videoRect.width - vw * cover) / 2;
        const offsetY = (videoRect.height - vh * cover) / 2;
        const roiX = ((frameRect.left - videoRect.left) - offsetX) / cover;
        const roiY = ((frameRect.top - videoRect.top) - offsetY) / cover;
        const roiW = frameRect.width / cover;
        const roiH = frameRect.height / cover;
        sx = Math.max(0, Math.min(vw - 1, roiX));
        sy = Math.max(0, Math.min(vh - 1, roiY));
        sw = Math.max(1, Math.min(vw - sx, roiW));
        sh = Math.max(1, Math.min(vh - sy, roiH));
      }

      // Downscale the cropped ROI for fast detection (decoded data unchanged).
      const dscale = Math.min(1, 480 / Math.max(sw, sh));
      const w = Math.max(1, Math.round(sw * dscale));
      const h = Math.max(1, Math.round(sh * dscale));
      if (canvas.width !== w) canvas.width = w;
      if (canvas.height !== h) canvas.height = h;

      let context = canvasCtxRef.current;
      if (!context) {
        context = canvas.getContext('2d', { willReadFrequently: true });
        canvasCtxRef.current = context;
      }
      context.drawImage(video, sx, sy, sw, sh, 0, 0, w, h);
      const imageData = context.getImageData(0, 0, w, h);
      const qrCode = jsQR(imageData.data, w, h, { inversionAttempts: 'dontInvert' });
      if (qrCode && qrCode.data && handleScanQRRef.current) {
        handleScanQRRef.current(qrCode.data);
      }
    } catch (error) {
      // Ignore transient frame/read errors and keep scanning.
    }
  }, []);

  useEffect(() => {
    if (!videoReady) return undefined;
    rafRef.current = requestAnimationFrame(scanFrame);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [videoReady, scanFrame]);

  if (authChecking || authError) {
    return (
      <div className="scanner-page">
        <div className="scanner-header">
          <div className="scanner-brand-row">
            <div className="scanner-logo">
              <img loading="lazy" src={getCmsImagePath("logo", "/logo.webp")} alt="festival logo" className="logo-img" />
            </div>
            <div className="scanner-header-copy">
              <h1 className="scanner-title">Festival Entry Scanner</h1>
              <p className="scanner-subtitle">
                {requireStaffAuth ? 'Staff check-in console' : 'Public scanner console'}
                {physicalGateLabel ? ` · ${physicalGateLabel} Gate` : ''}
              </p>
            </div>
          </div>
        </div>
        <div className="scanner-content">
          <div className="scanner-loading-card">
            {authError ? (
              <div className="scanner-error">
                <div className="error-icon">⚠️</div>
                <div className="error-text">{authError}</div>
                <div className="status-text">Redirecting...</div>
              </div>
            ) : (
              <div className="status-text">Checking authentication...</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`scanner-page ${showManualSmartScan ? 'scanner-page--manual' : ''}`}>
      {/*
      <div className="scanner-header">
        <div className="scanner-brand-row">
          <div className="scanner-logo">
            <img loading="lazy" src={getCmsImagePath("logo", "/logo.webp")} alt="festival logo" className="logo-img" />
          </div>
          <div className="scanner-header-copy">
            <h1 className="scanner-title">Festival Entry Scanner</h1>
            <p className="scanner-subtitle">Scan any QR code to validate and check in</p>
          </div>
        </div>
        <div className="scanner-header-badges">
          <span className={`scanner-badge ${videoReady ? 'is-live' : ''}`}>{videoReady ? 'Camera Live' : 'Connecting Camera'}</span>
          <span className={`scanner-badge ${scanning ? 'is-scanning' : ''}`}>{scanning ? 'Scanning...' : 'Ready'}</span>
        </div>
      </div>
      */}

      <div className="scanner-content">
        <div className="video-container">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="camera-feed"
            onLoadedData={handleVideoLoaded}
          />
          <div className="qr-frame" ref={qrFrameRef}>
            <div className="qr-corner qr-corner-tl"></div>
            <div className="qr-corner qr-corner-tr"></div>
            <div className="qr-corner qr-corner-bl"></div>
            <div className="qr-corner qr-corner-br"></div>
          </div>
          <div className="scanner-center-label">Align QR inside frame</div>
          <div className="scanner-overlay-top">
            <div className="scanner-overlay-top-row">
              {physicalGateLabel && (
                <div className={`scanner-overlay-pill scanner-overlay-pill--gate scanner-overlay-pill--gate-${gateTone}`}>
                  {physicalGateLabel} Gate
                </div>
              )}
              <div className="scanner-overlay-pill">Auto scan enabled</div>
            </div>
          </div>
          <div className="scanner-overlay-bottom">
            <div className="scanner-overlay-note">Best results: hold device steady 6–10 inches away</div>
          </div>

          {(error || scanning) && (
            <div className="scanner-feedback-layer">
              {error && (
                <div className="scanner-error scanner-feedback-card">
                  <div className="error-icon">⚠️</div>
                  <div className="error-text">{error}</div>
                </div>
              )}
              {scanning && (
                <div className="scanner-status scanner-feedback-card">
                  <div className="status-text">Validating ticket...</div>
                </div>
              )}
            </div>
          )}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>

        <div className="scanner-controls">
          <div className="scanner-controls-card">
            <div className="scanner-controls-copy">
              <p className="scanner-controls-title">Scanner Controls</p>
              <p className="scanner-controls-text">Use the flashlight in dim areas. Scanning starts automatically when a QR is visible.</p>
              <div className="scanner-day-selector">
                <p className="scanner-day-selector-title">Event Day</p>
                <p className="scanner-controls-text" style={{ margin: 0 }}>
                  Auto-detected from today&apos;s date
                </p>
              </div>
            </div>
            <button
              className={`btn-flashlight ${flashlightOn ? 'is-active' : ''}`}
              onClick={toggleFlashlight}
              title="Toggle Flashlight"
            >
              {flashlightOn ? 'Flashlight On' : 'Turn On Flashlight'}
            </button>
          </div>
        </div>

        {showManualSmartScan && (
          <div className="scanner-manual-wrap">
            <ManualDashboardScanPanel enabled />
          </div>
        )}
      </div>
    </div>
  );
};

export default Scanner;
